// Описание форм создания сущностей: по нему строится универсальная страница EntityCreateView
// и решается, показывать ли кнопку «Добавить» в списке.
//
// Ключ = путь списка (без «/»). Поля:
//   type:      text | email | password | tel | number | date | textarea | checkbox | select | multiselect | file | items
//   source:    URL справочника для select (значения берутся из data.data), value — поле id, optionLabel(o) — подпись
//   adminOnly: поле видит и заполняет только админ (у остальных ролей API само подставляет владельца)
//   onlyRoles: поле видят и заполняют только эти роли (например, адресат обращения — транспортник)
//   roles:     кто может создавать (по умолчанию только админ)
//   page:      своя страница создания вместо универсальной формы (кнопка «Добавить» ведёт на неё)
//   endpoint:  куда отправлять POST (по умолчанию «/ключ»); list — куда вернуться после создания

const ROLE_LABELS = { admin: "Администратор", client: "Клиент", guide: "Гид", insurer: "Страховщик", transporter: "Транспортник" };
const STATUS_LABELS = {
  draft: "Черновик", cart: "Корзина", pending_payment: "Ожидает оплаты", paid: "Оплачен",
  confirmed: "Подтверждён", cancelled: "Отменён", completed: "Завершён",
};

const rub = (value) => new Intl.NumberFormat("ru-RU", { style: "currency", currency: "RUB", maximumFractionDigits: 0 }).format(value || 0);
const shortId = (id) => `#${String(id).slice(0, 8)}`;

const userLabel = (u) => `${u.name} — ${ROLE_LABELS[u.userrole] || u.userrole} (${u.email})`;
const plainUserLabel = (u) => `${u.name} (${u.email})`;
const orderLabel = (o) => `${shortId(o.id)} · ${STATUS_LABELS[o.status] || o.status} · ${rub(o.total_amount)}`;
const orderItemLabel = (i) => `${i.title || "Позиция"} · ${i.quantity_persons} чел. · заказ ${shortId(i.order_id)}`;

const usersOf = (role) => ({ source: `/control?role=${role}&per_page=100`, optionLabel: plainUserLabel });
const allUsers = { source: "/control?per_page=100", optionLabel: userLabel };
const tours = { source: "/tour-templates?per_page=100", optionLabel: (t) => `${t.title} — ${rub(t.base_price)}` };
const extras = { source: "/extra-services?per_page=100", optionLabel: (e) => `${e.title} — ${rub(e.price_per_person)}/чел.` };
const orders = { source: "/orders?per_page=100", optionLabel: orderLabel };
const orderItems = { source: "/order-items?per_page=100", optionLabel: orderItemLabel };
const vehicles = { source: "/vehicles?per_page=100", optionLabel: (v) => `${v.name} — ${v.seats} мест` };

const ORDER_STATUSES = Object.entries(STATUS_LABELS).map(([value, label]) => ({ value, label }));

export const ENTITY_SCHEMAS = {
  control: {
    title: "Пользователь",
    fields: [
      { name: "name", label: "Имя", type: "text", required: true },
      { name: "email", label: "E-mail", type: "email", required: true },
      { name: "phone", label: "Телефон", type: "tel", placeholder: "+7 999 000-00-00" },
      {
        name: "role_id", label: "Роль", type: "select", required: true, source: "/roles",
        optionLabel: (r) => ROLE_LABELS[r.name] || r.name,
      },
      { name: "password", label: "Пароль", type: "password", required: true, hint: "Не менее 8 символов" },
    ],
  },

  "client-profiles": {
    title: "Профиль клиента",
    fields: [
      { name: "user_id", label: "Клиент", type: "select", required: true, adminOnly: true, ...usersOf("client") },
      { name: "passport_series", label: "Серия паспорта", type: "text", required: true, maxlength: 4, placeholder: "4510" },
      { name: "passport_number", label: "Номер паспорта", type: "text", required: true, maxlength: 6, placeholder: "123456" },
      { name: "passport_issued_by", label: "Кем выдан", type: "text", required: true },
      { name: "passport_issue_date", label: "Дата выдачи", type: "date", required: true },
      { name: "passport_expiry_date", label: "Действителен до", type: "date", required: true },
      { name: "phone", label: "Телефон", type: "tel", required: true },
      { name: "address", label: "Адрес", type: "text", required: true },
      { name: "emergency_contact", label: "Контакт для экстренной связи", type: "text" },
    ],
  },

  "guide-profiles": {
    title: "Профиль гида",
    fields: [
      { name: "user_id", label: "Гид", type: "select", required: true, adminOnly: true, ...usersOf("guide") },
      { name: "inn", label: "ИНН", type: "text", required: true, maxlength: 12, hint: "Обязателен всем гидам, в том числе самозанятым" },
      { name: "is_self_employed", label: "Самозанятый / без юридического лица", type: "checkbox", default: true },
      { name: "company_name", label: "Название юридического лица", type: "text", hint: "Только если есть юрлицо" },
      { name: "legal_address", label: "Юридический адрес", type: "text" },
      { name: "bank_name", label: "Банк", type: "text" },
      { name: "bik", label: "БИК", type: "text", maxlength: 9 },
      { name: "bank_account", label: "Расчётный счёт", type: "text" },
      { name: "correspondent_account", label: "Корреспондентский счёт", type: "text" },
      { name: "experience_years", label: "Опыт, лет", type: "number", required: true, default: 0, min: 0, max: 50 },
    ],
  },

  "insurer-profiles": {
    title: "Профиль страховщика",
    fields: [
      { name: "user_id", label: "Страховщик", type: "select", required: true, adminOnly: true, ...usersOf("insurer") },
      { name: "company_name", label: "Компания", type: "text", required: true },
      { name: "license_number", label: "Номер лицензии", type: "text", required: true },
      {
        name: "insurance_type", label: "Виды страхования", type: "multiselect", required: true,
        options: [
          { value: "travel", label: "Путешествия" }, { value: "medical", label: "Медицинское" },
          { value: "baggage", label: "Багаж" }, { value: "cancellation", label: "Отмена поездки" },
        ],
      },
      { name: "contact_person", label: "Контактное лицо", type: "text", required: true },
      { name: "phone", label: "Телефон", type: "tel", required: true },
      { name: "email", label: "E-mail компании", type: "email", required: true },
      { name: "address", label: "Адрес", type: "text", required: true },
      { name: "bank_details", label: "Банковские реквизиты", type: "textarea", required: true },
      { name: "rating", label: "Рейтинг (0–9.9)", type: "number", step: "0.1", min: 0, max: 9.9, adminOnly: true },
      { name: "is_active", label: "Активен", type: "checkbox", default: true, adminOnly: true },
    ],
  },

  "tour-templates": {
    title: "Шаблон тура",
    roles: ["admin", "guide"],
    page: "/tour-templates/create", // полный редактор с фото и гидом (views/admin/TourTemplateEdit.vue) вместо модального окна
    fields: [
      { name: "title", label: "Название", type: "text", required: true },
      { name: "description", label: "Описание", type: "textarea" },
      { name: "duration_days", label: "Длительность, дней", type: "number", required: true, min: 1, default: 3 },
      { name: "base_price", label: "Цена за человека, ₽", type: "number", required: true, min: 0 },
      { name: "is_individual", label: "Индивидуальный тур", type: "checkbox", default: false },
    ],
  },

  "extra-services": {
    title: "Дополнительная услуга",
    fields: [
      { name: "title", label: "Название", type: "text", required: true },
      { name: "price_per_person", label: "Цена за человека, ₽", type: "number", required: true, min: 0 },
      { name: "description", label: "Описание", type: "textarea" },
    ],
  },

  "vehicle-types": {
    title: "Тип транспорта",
    fields: [
      { name: "name", label: "Название", type: "text", required: true, placeholder: "Автобус 55" },
      { name: "description", label: "Описание", type: "text" },
    ],
  },

  vehicles: {
    title: "Транспортное средство",
    fields: [
      { name: "type_id", label: "Тип", type: "select", required: true, source: "/vehicle-types", optionLabel: (t) => t.name },
      { name: "name", label: "Название", type: "text", required: true, placeholder: "Автобус 55 мест" },
      { name: "seats", label: "Мест", type: "number", required: true, min: 1, max: 200 },
      { name: "plate_number", label: "Госномер", type: "text" },
      { name: "user_id", label: "Владелец (транспортник)", type: "select", ...usersOf("transporter") },
      { name: "photo", label: "Фото", type: "file", accept: "image/jpeg,image/png,image/webp", hint: "JPG, PNG или WebP до 4 МБ" },
      { name: "is_active", label: "Активен", type: "checkbox", default: true },
    ],
  },

  "vehicle-bookings": {
    title: "Бронирование транспорта",
    roles: ["admin", "guide"],
    fields: [
      { name: "vehicle_id", label: "Транспорт", type: "select", required: true, ...vehicles },
      { name: "date_from", label: "С", type: "date", required: true },
      { name: "date_to", label: "По", type: "date", required: true },
      { name: "destination", label: "Куда поездка", type: "text" },
      { name: "passengers", label: "Человек", type: "number", min: 1, max: 200 },
      { name: "booked_by", label: "Организатор", type: "select", adminOnly: true, ...allUsers },
      {
        name: "status", label: "Статус", type: "select", adminOnly: true, default: "reserved",
        options: [{ value: "reserved", label: "Зарезервировано" }, { value: "confirmed", label: "Подтверждено" }, { value: "cancelled", label: "Отменено" }],
      },
    ],
  },

  orders: {
    title: "Заказ",
    roles: ["admin", "guide", "client"],
    fields: [
      { name: "user_id", label: "Заказчик", type: "select", adminOnly: true, hint: "Если не выбран — заказ оформляется на вас", ...allUsers },
      { name: "status", label: "Статус", type: "select", adminOnly: true, default: "draft", options: ORDER_STATUSES },
      { name: "items", label: "Состав заказа", type: "items", required: true, hint: "Цены подставляются из каталога" },
    ],
  },

  "order-items": {
    title: "Позиция заказа",
    fields: [
      { name: "order_id", label: "Заказ", type: "select", required: true, ...orders },
      { name: "tour_template_id", label: "Тур", type: "select", hint: "Укажите тур или дополнительную услугу", ...tours },
      { name: "extra_service_id", label: "Дополнительная услуга", type: "select", ...extras },
      { name: "quantity_persons", label: "Человек", type: "number", required: true, min: 1, default: 1 },
      { name: "date_from", label: "С", type: "date" },
      { name: "date_to", label: "По", type: "date" },
    ],
  },

  "tour-participants": {
    title: "Участник тура",
    fields: [
      { name: "order_item_id", label: "Позиция заказа", type: "select", required: true, ...orderItems },
      { name: "full_name", label: "ФИО", type: "text", required: true },
      { name: "birth_date", label: "Дата рождения", type: "date", required: true },
      { name: "passport_data", label: "Паспортные данные", type: "text" },
    ],
  },

  carts: {
    title: "Позиция корзины",
    roles: ["admin", "guide", "client"],
    fields: [
      { name: "user_id", label: "Клиент", type: "select", adminOnly: true, hint: "Если не выбран — корзина ваша", ...allUsers },
      { name: "tour_template_id", label: "Тур", type: "select", required: true, ...tours },
      { name: "extra_service_id", label: "Дополнительная услуга", type: "select", ...extras },
      { name: "quantity_persons", label: "Человек", type: "number", required: true, min: 1, default: 1 },
      { name: "date_from", label: "С", type: "date", required: true },
      { name: "date_to", label: "По", type: "date", required: true },
    ],
  },

  "personal-offers": {
    title: "Персональное предложение",
    fields: [
      { name: "user_id", label: "Клиент", type: "select", required: true, ...usersOf("client") },
      { name: "tour_template_id", label: "Тур", type: "select", required: true, ...tours },
      { name: "title", label: "Заголовок", type: "text", required: true },
      { name: "description", label: "Описание", type: "textarea", required: true },
      { name: "discount_percent", label: "Скидка, %", type: "number", required: true, min: 0, max: 100, step: "0.5" },
      { name: "valid_from", label: "Действует с", type: "date" },
      { name: "valid_to", label: "Действует до", type: "date" },
      { name: "is_active", label: "Активно", type: "checkbox", default: true },
    ],
  },

  payments: {
    title: "Платёж",
    fields: [
      { name: "order_id", label: "Заказ", type: "select", required: true, ...orders },
      { name: "amount", label: "Сумма, ₽", type: "number", required: true, min: 0, step: "0.01" },
      {
        name: "payment_method", label: "Способ оплаты", type: "select", required: true, default: "bank_transfer",
        options: [
          { value: "card", label: "Банковская карта" }, { value: "bank_transfer", label: "Банковский перевод" },
          { value: "cash", label: "Наличные" }, { value: "online", label: "Онлайн / QR" },
        ],
      },
      {
        name: "status", label: "Статус", type: "select", default: "pending",
        hint: "Оплаченный платёж сразу учитывается в заказе",
        options: [{ value: "pending", label: "Ожидает оплаты" }, { value: "completed", label: "Оплачен" }, { value: "failed", label: "Неуспешный" }, { value: "refunded", label: "Возвращён" }],
      },
    ],
  },

  "insurance-requests": {
    title: "Страховой запрос",
    fields: [
      { name: "order_item_id", label: "Тур (позиция заказа)", type: "select", required: true, ...orderItems },
      { name: "insurer_id", label: "Страховщик", type: "select", required: true, ...usersOf("insurer") },
      { name: "insurance_amount", label: "Сумма страхования, ₽", type: "number", min: 0, step: "0.01", hint: "Обычно указывает страховщик" },
      { name: "notes", label: "Заметки", type: "textarea" },
    ],
  },

  "support-tickets": {
    title: "Обращение в поддержку",
    roles: ["admin", "client", "guide", "insurer", "transporter"],
    fields: [
      { name: "user_id", label: "От имени пользователя", type: "select", adminOnly: true, hint: "Если не выбран — от вашего имени", ...allUsers },
      {
        name: "recipient_id", label: "Кому", type: "select", onlyRoles: ["transporter", "admin"], source: "/support-tickets/recipients",
        optionLabel: (u) => `${u.name} — ${ROLE_LABELS[u.role] || u.role} (${u.email})`,
        hint: "Администратор, гид или клиент. Если не выбрано — обращение получит администратор",
      },
      { name: "subject", label: "Тема", type: "text", required: true },
      { name: "message", label: "Сообщение", type: "textarea", required: true },
    ],
  },

  documents: {
    title: "Документ",
    roles: ["admin", "client", "guide"], // как DocumentPolicy::create
    endpoint: "/documents/upload",
    list: "/documents",
    fields: [
      { name: "user_id", label: "Владелец", type: "select", adminOnly: true, hint: "Если не выбран — документ ваш", ...allUsers },
      {
        name: "type", label: "Тип документа", type: "select", required: true, default: "other",
        options: [
          { value: "passport", label: "Паспорт" }, { value: "inn", label: "ИНН" }, { value: "contract", label: "Договор" },
          { value: "license", label: "Лицензия" }, { value: "certificate", label: "Сертификат" }, { value: "other", label: "Другое" },
        ],
      },
      { name: "name", label: "Название", type: "text", hint: "По умолчанию — имя файла" },
      { name: "description", label: "Описание", type: "textarea" },
      { name: "file", label: "Файл", type: "file", required: true, hint: "До 10 МБ" },
    ],
  },

  notifications: {
    title: "Уведомление",
    fields: [
      { name: "user_id", label: "Кому", type: "select", required: true, ...allUsers },
      {
        name: "type", label: "Тип", type: "select", required: true, default: "system",
        options: [
          { value: "system", label: "Системное" }, { value: "order", label: "Заказ" }, { value: "payment", label: "Оплата" },
          { value: "booking", label: "Бронирование" }, { value: "insurance_request", label: "Страхование" },
          { value: "support_ticket", label: "Обращение" }, { value: "personal_offer", label: "Предложение" },
        ],
      },
      { name: "title", label: "Заголовок", type: "text", required: true },
      { name: "message", label: "Текст", type: "textarea", required: true },
    ],
  },
};

/** Может ли роль создавать сущность (показывать ли кнопку «Добавить» и открывать форму). */
export const canCreate = (entity, role) => {
  const schema = ENTITY_SCHEMAS[entity];

  return Boolean(schema) && (schema.roles || ["admin"]).includes(role);
};

/** Ключ сущности по пути списка: «/tour-templates» → «tour-templates». */
export const entityOfPath = (path) => path.replace(/^\/+/, "").split("/")[0];
